/**
 * ManuelleEingabeOverlay — Atomarer Tages-Editor für Zeitkonto (#816, v2.355.0).
 *
 * Ersetzt den alten Inline-Edit komplett: Kommt + Geht + Pausen als eine Einheit,
 * mit Live-Berechnung. Backend überschreibt alle Stempel des Tages in einer
 * Transaktion und legt Backup in stempel_korrekturen.
 *
 * @since v2.355.0
 */
import React, { useMemo, useState } from 'react';
import { X, AlertTriangle, Clock, Info, Plus, Trash2 } from 'lucide-react';

const timeRe = /^\d{2}:\d{2}$/;
const toMin = (hhmm) => {
  if (!timeRe.test(hhmm || '')) return null;
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
  return h * 60 + m;
};
const fmtHm = (min) => {
  if (min == null || Number.isNaN(min)) return '—';
  const sign = min < 0 ? '-' : '';
  const abs = Math.abs(min);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}:${String(m).padStart(2, '0')}`;
};

export default function ManuelleEingabeOverlay({
  mitarbeiterId,
  mitarbeiterName,
  datum,
  bestehendeDaten,
  hatBestehendeStempel,
  anzahlSessions = 0,
  tagesInfo = null,
  onClose,
  onSaved,
  t,
  locale,
}) {
  const [kommt, setKommt] = useState(bestehendeDaten?.kommt || '06:00');
  const [geht, setGeht] = useState(bestehendeDaten?.geht || '14:30');
  const [mittagMin, setMittagMin] = useState(bestehendeDaten?.mittag_min ?? 30);
  const [kurzMin, setKurzMin] = useState(bestehendeDaten?.pausen_min ?? 0);
  const [exakt, setExakt] = useState(false);
  const [mittagVon, setMittagVon] = useState('12:00');
  const [mittagBis, setMittagBis] = useState('12:30');
  const [kurzPausen, setKurzPausen] = useState([{ von: '', bis: '' }]);
  const [grund, setGrund] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sollMin = bestehendeDaten?.soll_min ?? 480;

  const wochentag = useMemo(() => {
    try {
      const d = new Date(datum + 'T00:00:00');
      return d.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { weekday: 'long' });
    } catch { return ''; }
  }, [datum, locale]);

  const datumLabel = useMemo(() => {
    try {
      const d = new Date(datum + 'T00:00:00');
      return d.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return datum; }
  }, [datum, locale]);

  // Live-Berechnung
  const calc = useMemo(() => {
    const kM = toMin(kommt);
    const gM = toMin(geht);
    if (kM == null || gM == null) return null;
    const brutto = gM - kM;

    let mittagEff = 0;
    let kurzEff = 0;
    if (exakt) {
      const mv = toMin(mittagVon); const mb = toMin(mittagBis);
      if (mv != null && mb != null && mb > mv) mittagEff = mb - mv;
      for (const kp of kurzPausen) {
        const v = toMin(kp.von); const b = toMin(kp.bis);
        if (v != null && b != null && b > v) kurzEff += (b - v);
      }
    } else {
      mittagEff = parseInt(mittagMin, 10) || 0;
      kurzEff = parseInt(kurzMin, 10) || 0;
    }
    const netto = brutto - mittagEff - kurzEff;
    return {
      brutto,
      pausen: mittagEff + kurzEff,
      mittag: mittagEff,
      kurz: kurzEff,
      netto,
      delta: netto - sollMin,
    };
  }, [kommt, geht, mittagMin, kurzMin, exakt, mittagVon, mittagBis, kurzPausen, sollMin]);

  const validate = () => {
    if (!timeRe.test(kommt) || !timeRe.test(geht)) return t('manuelleEingabe.errTime', 'Kommt und Geht müssen HH:MM sein');
    if (!calc) return t('manuelleEingabe.errTime', 'Ungültige Zeiten');
    if (calc.brutto <= 0) return t('manuelleEingabe.errOrder', 'Geht muss nach Kommt liegen');
    const bruttoH = calc.brutto / 60;
    if (bruttoH > 6 && calc.mittag < 30) return t('manuelleEingabe.errMittag30', 'Bei mehr als 6h Arbeit ist Mittagspause ≥ 30 min Pflicht');
    if (calc.netto <= 0) return t('manuelleEingabe.errNetto', 'Netto-Arbeitszeit muss größer als 0 sein');
    if (exakt) {
      const kM = toMin(kommt); const gM = toMin(geht);
      const mv = toMin(mittagVon); const mb = toMin(mittagBis);
      if (calc.mittag > 0) {
        if (mv == null || mb == null) return t('manuelleEingabe.errMittagTime', 'Mittagspause: Von/Bis müssen ausgefüllt sein');
        if (mb <= mv) return t('manuelleEingabe.errMittagOrder', 'Mittagspause: Bis muss nach Von liegen');
        if (mv < kM || mb > gM) return t('manuelleEingabe.errMittagRange', 'Mittagspause muss innerhalb Kommt-Geht liegen');
      }
      for (const [i, kp] of kurzPausen.entries()) {
        if (!kp.von && !kp.bis) continue;
        const v = toMin(kp.von); const b = toMin(kp.bis);
        if (v == null || b == null) return t('manuelleEingabe.errKurzTime', `Kurzpause ${i + 1}: Von/Bis müssen ausgefüllt sein`);
        if (b <= v) return t('manuelleEingabe.errKurzOrder', `Kurzpause ${i + 1}: Bis muss nach Von liegen`);
        if (v < kM || b > gM) return t('manuelleEingabe.errKurzRange', `Kurzpause ${i + 1}: muss innerhalb Kommt-Geht liegen`);
      }
    }
    if (grund.trim().length < 5) return t('manuelleEingabe.errGrund', 'Grund mind. 5 Zeichen');
    return null;
  };

  const validationError = validate();

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true); setError(null);
    try {
      let pausen_detail = null;
      if (exakt) {
        pausen_detail = [];
        if (calc.mittag > 0) pausen_detail.push({ typ: 'mittag', von: mittagVon, bis: mittagBis });
        for (const kp of kurzPausen) {
          if (kp.von && kp.bis) pausen_detail.push({ typ: 'kurz', von: kp.von, bis: kp.bis });
        }
      }
      const payload = {
        mitarbeiter_id: mitarbeiterId,
        datum,
        kommt,
        geht,
        mittag_minuten: calc.mittag,
        kurzpausen_minuten: calc.kurz,
        pausen_detail,
        grund: grund.trim(),
      };
      const res = await fetch('/api/zeitkonto/tageseingabe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Fehler beim Speichern');
      onSaved?.(data);
    } catch (err) {
      setError(err.message || t('manuelleEingabe.errSave', 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const addKurzPause = () => setKurzPausen(prev => [...prev, { von: '', bis: '' }]);
  const removeKurzPause = (i) => setKurzPausen(prev => prev.filter((_, idx) => idx !== i));
  const updateKurzPause = (i, field, val) => setKurzPausen(prev => prev.map((kp, idx) => idx === i ? { ...kp, [field]: val } : kp));

  const rowClass = 'flex items-center justify-between gap-4 py-2';
  const labelClass = 'text-sm font-medium text-[var(--text-secondary)]';
  const inputClass = 'w-28 px-2 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm font-mono bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20';
  const numInputClass = 'w-20 px-2 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm font-mono text-right bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-lg bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-card,#fff)] border-b border-[var(--border-default,#E5E7EB)] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-base font-bold text-[var(--text-primary)] flex items-center gap-2">
              <Clock size={18} className="text-[#338edc]" />
              {t('manuelleEingabe.title', 'Manuelle Tageseingabe')}
            </h2>
            <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
              {mitarbeiterName} — {datumLabel} ({wochentag})
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Kommt / Geht */}
          <div className="space-y-1">
            <div className={rowClass}>
              <label className={labelClass}>{t('manuelleEingabe.kommt', 'Kommt')}</label>
              <input type="time" value={kommt} onChange={e => setKommt(e.target.value)} className={inputClass} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>{t('manuelleEingabe.geht', 'Geht')}</label>
              <input type="time" value={geht} onChange={e => setGeht(e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* Pausen */}
          <div className="border-t border-[var(--border-default,#E5E7EB)] pt-3">
            <div className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-2">
              {t('manuelleEingabe.pausen', 'Pausen')}
            </div>

            {!exakt ? (
              <>
                <div className={rowClass}>
                  <label className={labelClass}>{t('manuelleEingabe.mittagspause', 'Mittagspause')}</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number" step="5" min="0" max="120"
                      value={mittagMin}
                      onChange={e => setMittagMin(e.target.value)}
                      className={numInputClass}
                    />
                    <span className="text-xs text-[var(--text-muted)]">min</span>
                  </div>
                </div>
                <div className={rowClass}>
                  <label className={labelClass}>{t('manuelleEingabe.kurzpausen', 'Kurzpausen')}</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number" step="5" min="0" max="60"
                      value={kurzMin}
                      onChange={e => setKurzMin(e.target.value)}
                      className={numInputClass}
                    />
                    <span className="text-xs text-[var(--text-muted)]">min</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <div>
                  <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">{t('manuelleEingabe.mittagspause', 'Mittagspause')}</div>
                  <div className="flex items-center gap-2 pl-2">
                    <span className="text-xs text-[var(--text-muted)]">{t('manuelleEingabe.von', 'Von')}</span>
                    <input type="time" value={mittagVon} onChange={e => setMittagVon(e.target.value)} className={inputClass} />
                    <span className="text-xs text-[var(--text-muted)]">{t('manuelleEingabe.bis', 'Bis')}</span>
                    <input type="time" value={mittagBis} onChange={e => setMittagBis(e.target.value)} className={inputClass} />
                    <span className="text-xs text-[var(--text-muted)] ml-auto">
                      {calc ? `${calc.mittag} min` : ''}
                    </span>
                  </div>
                </div>
                {kurzPausen.map((kp, i) => {
                  const vM = toMin(kp.von); const bM = toMin(kp.bis);
                  const dur = (vM != null && bM != null && bM > vM) ? (bM - vM) : null;
                  return (
                    <div key={i}>
                      <div className="text-xs font-medium text-[var(--text-secondary)] mb-1 flex items-center justify-between">
                        <span>{t('manuelleEingabe.kurzpause', 'Kurzpause')} {i + 1}</span>
                        {kurzPausen.length > 1 && (
                          <button onClick={() => removeKurzPause(i)} className="text-red-500 hover:text-red-700">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 pl-2">
                        <span className="text-xs text-[var(--text-muted)]">{t('manuelleEingabe.von', 'Von')}</span>
                        <input type="time" value={kp.von} onChange={e => updateKurzPause(i, 'von', e.target.value)} className={inputClass} />
                        <span className="text-xs text-[var(--text-muted)]">{t('manuelleEingabe.bis', 'Bis')}</span>
                        <input type="time" value={kp.bis} onChange={e => updateKurzPause(i, 'bis', e.target.value)} className={inputClass} />
                        <span className="text-xs text-[var(--text-muted)] ml-auto">{dur != null ? `${dur} min` : ''}</span>
                      </div>
                    </div>
                  );
                })}
                <button
                  onClick={addKurzPause}
                  className="text-xs text-[#338edc] hover:underline inline-flex items-center gap-1 pl-2"
                >
                  <Plus size={12} /> {t('manuelleEingabe.weitereKurzpause', 'Weitere Kurzpause hinzufügen')}
                </button>
              </div>
            )}

            <label className="flex items-center gap-2 mt-3 text-xs text-[var(--text-secondary)] cursor-pointer select-none">
              <input
                type="checkbox" checked={exakt} onChange={e => setExakt(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-[var(--border-default)]"
              />
              {t('manuelleEingabe.exakteZeiten', 'Exakte Pausen-Uhrzeiten angeben (optional)')}
            </label>
          </div>

          {/* Live-Berechnung */}
          {calc && (
            <div className="border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 space-y-1">
              <div className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">
                {t('manuelleEingabe.liveBerechnung', 'Live-Berechnung')}
              </div>
              <div className="flex justify-between text-xs"><span className="text-[var(--text-tertiary)]">{t('manuelleEingabe.brutto', 'Brutto')}</span><span className="font-mono text-[var(--text-secondary)]">{fmtHm(calc.brutto)}h</span></div>
              <div className="flex justify-between text-xs"><span className="text-[var(--text-tertiary)]">{t('manuelleEingabe.pausenGesamt', 'Pausen')}</span><span className="font-mono text-[var(--text-secondary)]">{fmtHm(calc.pausen)}h</span></div>
              <div className="flex justify-between text-sm font-semibold"><span className="text-[var(--text-primary)]">{t('manuelleEingabe.netto', 'Netto')}</span><span className="font-mono text-[var(--text-primary)]">{fmtHm(calc.netto)}h</span></div>
              <div className="flex justify-between text-xs"><span className="text-[var(--text-tertiary)]">{t('manuelleEingabe.soll', 'SOLL')}</span><span className="font-mono text-[var(--text-secondary)]">{fmtHm(sollMin)}h</span></div>
              <div className="flex justify-between text-xs font-medium">
                <span className="text-[var(--text-tertiary)]">{t('manuelleEingabe.delta', 'Delta')}</span>
                <span className={`font-mono ${calc.delta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{calc.delta >= 0 ? '+' : ''}{fmtHm(calc.delta)}h</span>
              </div>
            </div>
          )}

          {/* Grund */}
          <div className="border-t border-[var(--border-default,#E5E7EB)] pt-3">
            <label className="block text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">
              {t('manuelleEingabe.grund', 'Grund')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={grund}
              onChange={e => { setGrund(e.target.value); if (error) setError(null); }}
              placeholder={t('manuelleEingabe.grundPlaceholder', 'z.B. Nachträgliche Erfassung')}
              className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
            />
            <div className="text-[10px] text-[var(--text-muted)] mt-1">{t('manuelleEingabe.grundHint', 'Pflichtfeld, min. 5 Zeichen')}</div>
          </div>

          {/* v2.360.0 (#822) P1-1: Warnung bei Feiertag oder Ganztags-Abwesenheit */}
          {tagesInfo?.feiertag_name && (
            <div className="flex items-start gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <AlertTriangle size={14} className="text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-purple-800 dark:text-purple-300">
                {t('manuelleEingabe.warnFeiertag', 'Dieser Tag ist Feiertag ({{name}}). Manuelle Erfassung markiert den Tag als "Feiertag gearbeitet" — bitte prüfen, ob Feiertagszuschlag gilt.').replace('{{name}}', tagesInfo.feiertag_name)}
              </div>
            </div>
          )}
          {tagesInfo?.abwesenheit_label && !tagesInfo?.feiertag_name && (
            <div className="flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <AlertTriangle size={14} className="text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-orange-800 dark:text-orange-300">
                {t('manuelleEingabe.warnAbwesenheit', 'Für diesen Tag ist eine Abwesenheit ({{label}}) hinterlegt. Manuelle Zeit-Erfassung überschreibt die Abwesenheit NICHT — bitte Abwesenheit anpassen, falls gearbeitet wurde.').replace('{{label}}', tagesInfo.abwesenheit_label)}
              </div>
            </div>
          )}

          {/* Warnung bei bestehenden Stempeln */}
          {hatBestehendeStempel && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800 dark:text-amber-300">
                {anzahlSessions >= 2
                  ? t('manuelleEingabe.warnOverwriteSessions', '{{n}} bestehende Sessions werden überschrieben. Bisherige Daten werden als Backup gespeichert.').replace('{{n}}', anzahlSessions)
                  : t('manuelleEingabe.warnOverwrite', 'Bestehende Stempel werden überschrieben. Bisherige Daten werden als Backup gespeichert.')}
              </div>
            </div>
          )}

          {!hatBestehendeStempel && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800 dark:text-blue-300">
                {t('manuelleEingabe.infoNew', 'Keine bestehenden Stempel — Tag wird neu angelegt.')}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-700 dark:text-red-300">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[var(--bg-card,#fff)] border-t border-[var(--border-default,#E5E7EB)] px-6 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !!validationError}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-[#338edc] hover:bg-[#2b7bc4] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            title={validationError || ''}
          >
            {saving ? '...' : t('manuelleEingabe.speichern', 'Speichern')}
          </button>
        </div>
      </div>
    </div>
  );
}
